import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addJobCategory,
  getJobCategoryDataById,
  updateJobCategory,
} from "model/CategoryModel";
import Select from "react-select";
import { addLegends, getAllLegendsUsingId, updateLegends } from "model/AuditingParameterModel";


/* -------------------- Yup Schema -------------------- */
const legendsSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Title is required"),
  status: Yup.boolean(),
});

const LegendsAddEditModal = ({
  setLegendsAddEditModalOpen,
  legendsAddEditModalOpen,
  getLegendsList,
  itemId,
  setFormData,
  formData,
  totalData
}) => {
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});

  const closeModal = () => {
    setLegendsAddEditModalOpen(false);
    setErrors({});
  };

  /* -------------------- Change Handler -------------------- */
  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (!legendsSchema.fields[name]) return;

    try {
      await Yup.reach(legendsSchema, name).validate(fieldValue);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await legendsSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = {
      title: formData?.title?.trim(),
      status: Boolean(formData?.status),
    };

    let response;
    if (itemId) {
      response = await updateLegends(itemId, params);
    } else {
      response = await addLegends(params);
    }

    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getLegendsList();
    } else {
      toast.error(response?.message);
    }
  };

  /* -------------------- Edit Fetch -------------------- */
  const getFetchLegendById = async (id) => {
    const response = await getAllLegendsUsingId({ Id: id });
    if (response?.data) {
      setFormData({
        ...response?.data,
        status:
          response?.data?.status === "active" ||
          response?.data?.status === true,
        title: response?.data?.title
      });
      setFormKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (itemId) {
      getFetchLegendById(itemId);
    }
  }, [itemId]);

  return (
    <Modal
      isOpen={legendsAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{itemId? 'Edit' : 'Create'} Legends</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          {/* Name */}
          <FormGroup>
            <Label>Title <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="title"
              defaultValue={formData?.title}
              onChange={handleChange}
            />
            {errors.title && (
              <small className="text-danger">{errors.title}</small>
            )}
          </FormGroup>

          {/* Status */}
          <FormGroup>
            <Label>Status <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData?.status ? "Active" : "Deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    defaultChecked={formData?.status}
                    onChange={handleChange}
                    className="form-check-input-padding"
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
            {itemId? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default LegendsAddEditModal;
